import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { nextStep, ONBOARDING_BASE_PATH } from "../registry";
import { useFlowState, useFlowActions } from "../../state/FlowProvider";
import PolicyConsentDialog from "../../components/PolicyConsentDialog";
import {
  evaluatePassword,
  passwordIsValid,
  emailIsValid,
} from "../../password";
import { onboardingTokens } from "../../theme";
import { fetchCurrentPolicies, registerAccount } from "../../api";
import { buildConsentAcknowledgements } from "../../completionPayload";
import { t } from "../../../../locales/t";
import FormSubmitButton from "../../components/FormSubmitButton";

export const POLICIES = ["terms", "privacy", "community"];

const loadPolicies = async () => {
  const policies = await fetchCurrentPolicies();
  if (
    !POLICIES.every(
      (type) =>
        policies.filter((policy) => policy.type === type).length === 1 &&
        policies.find((policy) => policy.type === type)?.body?.trim() &&
        Number.isInteger(
          policies.find((policy) => policy.type === type)?.revision,
        ),
    )
  ) {
    throw new Error("Current policies are unavailable");
  }
  return policies;
};

/**
 * Account creation: credentials plus the three policy consents.
 *
 * The password never enters flow state. Flow state is written to sessionStorage
 * on every change, and a password sitting in storage for the life of the tab is
 * not a trade worth making to survive a refresh — it is held here and submitted.
 *
 * Each policy is accepted separately after reading its document to the end.
 */
const CreateAccountStep = ({ step }) => {
  const state = useFlowState();
  const { setEmail, setConsents } = useFlowActions();
  const navigate = useNavigate();

  const [email, setEmailField] = useState(state.email ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [openPolicy, setOpenPolicy] = useState(null);
  const [policyState, setPolicyState] = useState({
    status: "loading",
    policies: [],
  });
  const [policyRetry, setPolicyRetry] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadPolicies()
      .then((policies) => {
        if (!cancelled) setPolicyState({ status: "ready", policies });
      })
      .catch(() => {
        if (!cancelled) setPolicyState({ status: "error", policies: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [policyRetry]);

  const rules = evaluatePassword(password);
  const emailOk = emailIsValid(email);
  const passwordsMatch = password.length > 0 && password === confirm;
  const policiesReady = policyState.status === "ready";
  const allAccepted =
    policiesReady &&
    POLICIES.every(
      (id) =>
        state.consents?.[id]?.version ===
        policyState.policies.find((policy) => policy.type === id)?.revision,
    );

  const canSubmit =
    emailOk &&
    passwordIsValid(password) &&
    passwordsMatch &&
    allAccepted &&
    !submitting;

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  // Prevent focus shift on password reveal icons
  const handleMouseDownPassword = (e) => e.preventDefault();
  const handleMouseUpPassword = (e) => e.preventDefault();

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      // A published revision can change after this page loads. Never submit
      // receipts for a different set than the one the member acknowledged.
      let currentPolicies;
      try {
        currentPolicies = await loadPolicies();
      } catch {
        setConsents({});
        setPolicyState({ status: "error", policies: [] });
        setSubmitting(false);
        return;
      }
      if (
        POLICIES.some(
          (id) =>
            currentPolicies.find((policy) => policy.type === id)?.revision !==
            state.consents[id]?.version,
        )
      ) {
        setPolicyState({ status: "ready", policies: currentPolicies });
        setConsents({});
        setSubmitError("policyChanged");
        setSubmitting(false);
        return;
      }
      // The 16+ age gate is enforced at registration, so the DOB collected
      // earlier travels with this request.
      await registerAccount({
        email,
        password,
        dateOfBirth: state.dob,
        consents: buildConsentAcknowledgements(state.consents, POLICIES),
      });

      setEmail(email);

      const next = nextStep(step.id, { ...state, email });
      if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);

      // Deliberately not reset on the success path, and no `finally`: the
      // navigate unmounts this screen, so clearing the flag afterwards would be
      // a state update on a component that no longer exists. Leaving it set
      // also keeps the button inert during the transition.
    } catch (error) {
      // Only the known duplicate-email response gets a specific action. Other
      // server errors are not suitable for display as member-facing copy.
      const duplicateEmail =
        error?.status === 409 &&
        error?.message?.trim().toLowerCase() === "email already taken";
      if (error?.status === 409 && !duplicateEmail) {
        setConsents({});
        setPolicyState({ status: "loading", policies: [] });
        setPolicyRetry((retry) => retry + 1);
      }
      setSubmitError(
        duplicateEmail
          ? "duplicateEmail"
          : error?.status === 409
            ? "policyChanged"
            : "failed",
      );
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <Typography variant="h1" sx={{ mb: 1.5 }}>
        {t("onboarding.createAccount.heading")}
      </Typography>

      <Typography sx={{ color: onboardingTokens.mutedForeground, mb: 4 }}>
        {t("onboarding.createAccount.body")}
      </Typography>

      <Stack gap={2.5}>
        <TextField
          label={t("onboarding.createAccount.email")}
          type="email"
          value={email}
          onChange={(e) => setEmailField(e.target.value)}
          error={email.length > 0 && !emailOk}
          helperText={
            email.length > 0 && !emailOk
              ? t("onboarding.createAccount.emailInvalid")
              : " "
          }
          fullWidth
        />

        <Box>
          <TextField
            label={t("onboarding.createAccount.password")}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      color="secondary"
                      aria-label={
                        showPassword
                          ? t("onboarding.createAccount.passwordHideAriaLabel")
                          : t("onboarding.createAccount.passwordShowAriaLabel")
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      onMouseUp={handleMouseUpPassword}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Each rule reported independently, so "your password is invalid"
              is never the only thing the member is told. Icon plus text, not
              colour alone. */}
          <Stack gap={0.5} sx={{ mt: 1.5 }} aria-live="polite">
            {rules.map(({ id, met }) => (
              <Stack key={id} direction="row" alignItems="center" gap={1}>
                {met ? (
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: onboardingTokens.teal }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    fontSize="small"
                    sx={{ color: onboardingTokens.mutedForeground }}
                  />
                )}
                <Typography
                  data-testid={`password-rule-${id}`}
                  data-met={met ? "true" : "false"}
                  sx={{
                    fontSize: "0.8125rem",
                    color: met
                      ? onboardingTokens.navy
                      : onboardingTokens.mutedForeground,
                  }}
                >
                  {t(`onboarding.createAccount.rules.${id}`)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <TextField
          label={t("onboarding.createAccount.confirm")}
          type={showConfirmPassword ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm.length > 0 && !passwordsMatch}
          helperText={
            confirm.length > 0 && !passwordsMatch
              ? t("onboarding.createAccount.confirmMismatch")
              : " "
          }
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type="button"
                    color="secondary"
                    aria-label={
                      showConfirmPassword
                        ? t("onboarding.createAccount.passwordHideAriaLabel")
                        : t("onboarding.createAccount.passwordShowAriaLabel")
                    }
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Stack
          gap={1.25}
          sx={{
            p: 2,
            borderRadius: "0.75rem",
            backgroundColor: onboardingTokens.lightTeal,
            border: `1px solid ${onboardingTokens.border}`,
          }}
        >
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {t("onboarding.createAccount.policiesHeading")}
          </Typography>

          {POLICIES.map((id) => (
            <FormControlLabel
              key={id}
              sx={{ display: "flex", ml: 0 }}
              control={
                <Checkbox
                  checked={
                    policiesReady &&
                    state.consents?.[id]?.version ===
                      policyState.policies.find((policy) => policy.type === id)
                        ?.revision
                  }
                  disabled={!policiesReady || submitting}
                  readOnly
                  onClick={(event) => {
                    event.preventDefault();
                    setOpenPolicy(id);
                  }}
                  inputProps={{
                    "aria-label": t(`onboarding.policies.${id}.title`),
                  }}
                />
              }
              label={
                <Typography sx={{ cursor: "pointer", fontSize: "0.875rem" }}>
                  {t("onboarding.policies.consentPrefix")}
                  <Box
                    component="span"
                    sx={{
                      color: onboardingTokens.teal,
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    {t(`onboarding.policies.${id}.title`)}
                  </Box>
                </Typography>
              }
            />
          ))}
          {policyState.status === "loading" && (
            <Typography>{t("onboarding.policies.loading")}</Typography>
          )}
          {policyState.status === "error" && (
            <Alert severity="error">
              {t("onboarding.policies.loadFailed")}{" "}
              <Button
                type="button"
                onClick={() => {
                  setPolicyState({ status: "loading", policies: [] });
                  setPolicyRetry((retry) => retry + 1);
                }}
              >
                {t("onboarding.policies.retry")}
              </Button>
            </Alert>
          )}
        </Stack>

        {submitError && (
          <Alert severity="error">
            {submitError === "duplicateEmail" ? (
              <>
                {t("onboarding.createAccount.duplicateEmail")}{" "}
                <Link to="/login">{t("onboarding.createAccount.signIn")}</Link>
                {t("onboarding.createAccount.signInSuffix")}
              </>
            ) : submitError === "policyChanged" ? (
              t("onboarding.policies.changed")
            ) : (
              t("onboarding.createAccount.failed")
            )}
          </Alert>
        )}

        <FormSubmitButton
          variant="contained"
          fullWidth
          disabled={submitting}
          disabledReason={
            !submitting && !canSubmit
              ? t("onboarding.createAccount.incomplete")
              : ""
          }
          sx={{ py: 1.5 }}
        >
          {t("onboarding.createAccount.submit")}
        </FormSubmitButton>
      </Stack>

      <PolicyConsentDialog
        key={openPolicy}
        policy={policyState.policies.find(
          (policy) => policy.type === openPolicy,
        )}
        onClose={() => setOpenPolicy(null)}
        onAccept={(id, version) => {
          setConsents({
            ...state.consents,
            [id]: { version, acceptedAt: new Date().toISOString() },
          });
          setSubmitError(null);
          setOpenPolicy(null);
        }}
      />
    </Box>
  );
};

export default CreateAccountStep;
